import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@design-system';
import { toast } from 'sonner';
import {
  MonitorRequestQuoteSummary,
  MonitorRequestRow,
} from '@/features/illustration-monitor/types';

interface RequestDetailDialogProps {
  row: MonitorRequestRow | null;
  onClose: () => void;
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function formatCurrency(value: string | null) {
  if (value === null) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });
}

function computeLatencyMs(row: MonitorRequestRow): number | null {
  if (!row.responseDateTime) return null;
  return new Date(row.responseDateTime).getTime() - new Date(row.createdDateTime).getTime();
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-0.5 text-sm text-gray-900">{value}</div>
    </div>
  );
}

function copyToClipboard(value: string, label: string) {
  void navigator.clipboard.writeText(value).then(
    () => toast.success(`${label} copied`),
    () => toast.error(`Failed to copy ${label}`),
  );
}

function QuoteBlock({ quote }: { quote: MonitorRequestQuoteSummary }) {
  const isLTC = quote.quoteType === 'traditionalLTC';
  return (
    <div className="rounded-md border border-gray-200 p-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Quote</div>
        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
          {isLTC ? 'Traditional LTC' : 'Insurance'}
        </span>
      </div>
      <div className="mt-1 text-sm font-medium text-gray-900">
        {quote.carrierName} · {quote.productName}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
        <Field
          label="State"
          value={quote.state}
        />
        <Field
          label={isLTC ? 'Modal premium' : 'Premium'}
          value={formatCurrency(quote.premiumAmount) ?? quote.premiumAmount}
        />
        <Field
          label="Primary age"
          value={quote.primaryAge}
        />
        <Field
          label="Primary gender"
          value={quote.primaryGender}
        />
        {quote.policyType && (
          <Field
            label="Policy type"
            value={quote.policyType}
          />
        )}
        {!isLTC && quote.ltcBenefitTotal && (
          <Field
            label="LTC benefit total"
            value={formatCurrency(quote.ltcBenefitTotal) ?? quote.ltcBenefitTotal}
          />
        )}
        {!isLTC && quote.cobPaymentOption && (
          <Field
            label="Payment option"
            value={quote.cobPaymentOption}
          />
        )}
        {isLTC && quote.benefitPool && (
          <Field
            label="Benefit pool"
            value={formatCurrency(quote.benefitPool) ?? quote.benefitPool}
          />
        )}
        {isLTC && quote.premiumFrequency && (
          <Field
            label="Premium frequency"
            value={quote.premiumFrequency}
          />
        )}
        {isLTC && quote.underwritingClass && (
          <Field
            label="Underwriting class"
            value={quote.underwritingClass}
          />
        )}
      </div>
    </div>
  );
}

export function RequestDetailDialog({ row, onClose }: RequestDetailDialogProps) {
  const open = !!row;

  return (
    <Dialog
      open={open}
      onOpenChange={next => (!next ? onClose() : undefined)}
    >
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-auto">
        {row && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span>Illustration request</span>
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                    row.status === 'completed'
                      ? 'bg-green-100 text-green-800'
                      : row.status === 'error'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {row.status}
                </span>
              </DialogTitle>
            </DialogHeader>

            <div className="mt-2 space-y-4">
              <div className="flex items-center gap-2">
                <code className="flex-1 truncate rounded bg-gray-100 px-2 py-1 text-xs">
                  {row.requestId}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(row.requestId, 'Request ID')}
                >
                  Copy
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field
                  label="Created"
                  value={formatDate(row.createdDateTime)}
                />
                <Field
                  label="Responded"
                  value={formatDate(row.responseDateTime)}
                />
                <Field
                  label="Latency"
                  value={
                    computeLatencyMs(row) !== null
                      ? `${(computeLatencyMs(row)! / 1000).toFixed(2)}s`
                      : '—'
                  }
                />
                <Field
                  label="Quote ID"
                  value={<span className="font-mono text-xs">{row.insuranceQuoteId}</span>}
                />
              </div>

              <div className="rounded-md border border-gray-200 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Client
                </div>
                {row.client ? (
                  <>
                    <div className="mt-1 text-sm text-gray-900">
                      {row.client.clientFirstName ?? ''} {row.client.clientLastName}
                    </div>
                    <div className="mt-1 font-mono text-xs text-gray-500">
                      {row.clientId} · advisor {row.client.advisorId}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mt-1 text-sm italic text-gray-500">(client not found)</div>
                    <div className="mt-1 font-mono text-xs text-gray-500">{row.clientId}</div>
                  </>
                )}
              </div>

              <div className="rounded-md border border-gray-200 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Client Advisor
                </div>
                {row.advisor ? (
                  <>
                    <div className="mt-1 text-sm text-gray-900">
                      {row.advisor.advisorFirstName ?? ''} {row.advisor.advisorLastName ?? ''}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">{row.advisor.advisorEmail}</div>
                    <div className="mt-1 font-mono text-xs text-gray-500">
                      {row.advisor.advisorId}
                    </div>
                  </>
                ) : (
                  <div className="mt-1 text-sm italic text-gray-500">(advisor not found)</div>
                )}
              </div>

              {row.quote && <QuoteBlock quote={row.quote} />}

              {row.pdfUrl && (
                <Field
                  label="PDF"
                  value={
                    <a
                      href={row.pdfUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary underline"
                    >
                      Open PDF
                    </a>
                  }
                />
              )}

              {row.fileLocation && (
                <Field
                  label="File location"
                  value={<span className="font-mono text-xs">{row.fileLocation}</span>}
                />
              )}

              {row.error && (
                <details className="rounded-md border border-red-200 bg-red-50 p-3">
                  <summary className="cursor-pointer text-sm font-semibold text-red-800">
                    Error
                  </summary>
                  <pre className="mt-2 overflow-auto whitespace-pre-wrap text-xs text-red-900">
                    {row.error}
                  </pre>
                </details>
              )}

              {row.responseData !== null && row.responseData !== undefined && (
                <details className="rounded-md border border-gray-200 bg-gray-50 p-3">
                  <summary className="cursor-pointer text-sm font-semibold text-gray-700">
                    Response data
                  </summary>
                  <pre className="mt-2 overflow-auto text-xs text-gray-800">
                    {JSON.stringify(row.responseData, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
